// frontend/src/settings/JobTypeSettings.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../supabase';
import { useOrganization } from '../contexts/OrganizationContext';
import { writeClientAudit } from '../utils/auditClient';
import Header from '../components/ui/Header';

function JobTypeSettings() {
  const { currentOrganization } = useOrganization();
  const [jobTypes, setJobTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    name: '',
    description: '',
    is_surgery_assignable: false,
    has_skill_evaluation: false,
    display_order: 0,
  });
  const [isAdding, setIsAdding] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (currentOrganization?.id) fetchJobTypes();
  }, [currentOrganization]);

  const fetchJobTypes = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('job_types')
        .select('*')
        .eq('organization_id', currentOrganization.id)
        .order('display_order', { ascending: true });
      if (error) throw error;
      setJobTypes(data || []);
    } catch (err) {
      console.error('職種取得エラー:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => setForm({ name: '', description: '', is_surgery_assignable: false, has_skill_evaluation: false, display_order: 0 });

  const handleAdd = async () => {
    if (!form.name.trim()) { alert('職種名を入力してください'); return; }
    setSaving(true);
    try {
      const { error } = await supabase.from('job_types').insert({
        organization_id: currentOrganization.id,
        name: form.name.trim(),
        description: form.description.trim(),
        is_surgery_assignable: form.is_surgery_assignable,
        has_skill_evaluation: form.has_skill_evaluation,
        display_order: form.display_order || jobTypes.length + 1,
      });
      if (error) throw error;
      await writeClientAudit({
        organizationId: currentOrganization.id,
        actionType: 'create',
        targetTable: 'job_types',
        targetSummary: `職種追加: ${form.name.trim()}`,
        changes: { name: form.name.trim(), is_surgery_assignable: form.is_surgery_assignable, has_skill_evaluation: form.has_skill_evaluation },
        success: true,
      });
      resetForm();
      setIsAdding(false);
      await fetchJobTypes();
    } catch (err) {
      alert('追加に失敗しました: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (jt) => {
    setEditingId(jt.id);
    setForm({
      name: jt.name,
      description: jt.description || '',
      is_surgery_assignable: jt.is_surgery_assignable || false,
      has_skill_evaluation: jt.has_skill_evaluation || false,
      display_order: jt.display_order || 0,
    });
    setIsAdding(false);
  };

  const handleSaveEdit = async () => {
    if (!form.name.trim()) { alert('職種名を入力してください'); return; }
    setSaving(true);
    try {
      const { error } = await supabase.from('job_types').update({
        name: form.name.trim(),
        description: form.description.trim(),
        is_surgery_assignable: form.is_surgery_assignable,
        has_skill_evaluation: form.has_skill_evaluation,
        display_order: form.display_order,
        updated_at: new Date().toISOString(),
      }).eq('id', editingId);
      if (error) throw error;
      await writeClientAudit({
        organizationId: currentOrganization.id,
        actionType: 'update',
        targetTable: 'job_types',
        targetId: editingId,
        targetSummary: `職種更新: ${form.name.trim()}`,
        changes: { name: form.name.trim(), is_surgery_assignable: form.is_surgery_assignable, has_skill_evaluation: form.has_skill_evaluation },
        success: true,
      });
      setEditingId(null);
      resetForm();
      await fetchJobTypes();
    } catch (err) {
      alert('更新に失敗しました: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`「${name}」を削除しますか？\nこの職種に所属するスタッフの職種設定が解除されます。`)) return;
    try {
      const { error } = await supabase.from('job_types').delete().eq('id', id);
      if (error) throw error;
      await writeClientAudit({
        organizationId: currentOrganization.id,
        actionType: 'delete',
        targetTable: 'job_types',
        targetId: id,
        targetSummary: `職種削除: ${name}`,
        success: true,
      });
      await fetchJobTypes();
    } catch (err) {
      alert('削除に失敗しました: ' + err.message);
    }
  };

  const styles = {
    container: { padding: '2rem', maxWidth: '800px', margin: '0 auto', fontFamily: 'sans-serif' },
    backLink: { display: 'inline-block', marginBottom: '1rem', color: '#007bff', textDecoration: 'none' },
    card: { backgroundColor: '#fff', borderRadius: '8px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', padding: '2rem', marginTop: '1.5rem', border: '1px solid #e0e0e0' },
    header: { borderBottom: '1px solid #eee', paddingBottom: '1rem', marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    addBtn: { padding: '0.5rem 1rem', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.9rem' },
    input: { width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc', fontSize: '0.95rem', boxSizing: 'border-box' },
    label: { display: 'block', marginBottom: '0.25rem', fontWeight: '500', color: '#333', fontSize: '0.9rem' },
    formRow: { marginBottom: '0.75rem' },
    checkboxRow: { display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' },
    saveBtn: { padding: '0.5rem 1rem', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.9rem' },
    cancelBtn: { padding: '0.5rem 1rem', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.9rem', marginLeft: '0.5rem' },
    deleteBtn: { padding: '0.4rem 0.75rem', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem' },
    editBtn: { padding: '0.4rem 0.75rem', backgroundColor: '#ffc107', color: '#333', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem', marginRight: '0.5rem' },
    badge: { display: 'inline-block', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', marginLeft: '4px' },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #eee', fontSize: '0.9rem', color: '#555' },
    td: { padding: '0.75rem', borderBottom: '1px solid #f0f0f0', fontSize: '0.9rem' },
  };

  return (
    <div style={styles.container}>
      <Header />
      <Link to="/settings" style={styles.backLink}>← 設定メニューへ戻る</Link>

      <div style={styles.card}>
        <div style={styles.header}>
          <h2 style={{ margin: 0 }}>職種設定</h2>
          {!isAdding && !editingId && (
            <button onClick={() => setIsAdding(true)} style={styles.addBtn}>＋ 職種を追加</button>
          )}
        </div>

        {/* ✅ FormSectionをインラインに展開（コンポーネント分離をやめる） */}
        {(isAdding || editingId) && (
          <div style={{ backgroundColor: '#f8f9fa', padding: '1rem', borderRadius: '6px', marginBottom: '1.5rem', border: '1px solid #dee2e6' }}>
            <div style={styles.formRow}>
              <label style={styles.label}>職種名 *</label>
              <input
                type="text"
                value={form.name}
                onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                style={styles.input}
                placeholder="例：看護師"
              />
            </div>
            <div style={styles.formRow}>
              <label style={styles.label}>説明</label>
              <input
                type="text"
                value={form.description}
                onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
                style={styles.input}
                placeholder="例：手術室看護師"
              />
            </div>
            <div style={styles.formRow}>
              <label style={styles.label}>表示順</label>
              <input
                type="number"
                value={form.display_order}
                onChange={e => setForm(prev => ({ ...prev, display_order: parseInt(e.target.value) || 0 }))}
                style={{ ...styles.input, width: '80px' }}
                min="0"
              />
            </div>
            <div style={styles.checkboxRow}>
              <input
                type="checkbox"
                id="surgery"
                checked={form.is_surgery_assignable}
                onChange={e => setForm(prev => ({ ...prev, is_surgery_assignable: e.target.checked }))}
              />
              <label htmlFor="surgery" style={{ cursor: 'pointer', fontSize: '0.9rem' }}>手術配置対象（シフト作成の対象になる）</label>
            </div>
            <div style={styles.checkboxRow}>
              <input
                type="checkbox"
                id="skill"
                checked={form.has_skill_evaluation}
                onChange={e => setForm(prev => ({ ...prev, has_skill_evaluation: e.target.checked }))}
              />
              <label htmlFor="skill" style={{ cursor: 'pointer', fontSize: '0.9rem' }}>スキル評価対象（スキル記録を管理する）</label>
            </div>
            <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
              {editingId ? (
                <>
                  <button onClick={handleSaveEdit} disabled={saving} style={styles.saveBtn}>{saving ? '保存中...' : '更新'}</button>
                  <button onClick={() => { setEditingId(null); resetForm(); }} style={styles.cancelBtn}>キャンセル</button>
                </>
              ) : (
                <>
                  <button onClick={handleAdd} disabled={saving} style={styles.saveBtn}>{saving ? '追加中...' : '追加'}</button>
                  <button onClick={() => { setIsAdding(false); resetForm(); }} style={styles.cancelBtn}>キャンセル</button>
                </>
              )}
            </div>
          </div>
        )}

        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#999' }}>読み込み中...</div>
        ) : jobTypes.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#999' }}>職種が登録されていません</div>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>順</th>
                <th style={styles.th}>職種名</th>
                <th style={styles.th}>説明</th>
                <th style={styles.th}>属性</th>
                <th style={styles.th}>操作</th>
              </tr>
            </thead>
            <tbody>
              {jobTypes.map((jt) => (
                <tr key={jt.id}>
                  <td style={styles.td}>{jt.display_order}</td>
                  <td style={{ ...styles.td, fontWeight: '600' }}>{jt.name}</td>
                  <td style={{ ...styles.td, color: '#666' }}>{jt.description || '-'}</td>
                  <td style={styles.td}>
                    {jt.is_surgery_assignable && (
                      <span style={{ ...styles.badge, backgroundColor: '#dbeafe', color: '#1565c0' }}>手術配置</span>
                    )}
                    {jt.has_skill_evaluation && (
                      <span style={{ ...styles.badge, backgroundColor: '#dcfce7', color: '#166534' }}>スキル評価</span>
                    )}
                    {!jt.is_surgery_assignable && !jt.has_skill_evaluation && (
                      <span style={{ color: '#999', fontSize: '12px' }}>なし</span>
                    )}
                  </td>
                  <td style={styles.td}>
                    <button onClick={() => handleEdit(jt)} style={styles.editBtn} disabled={!!editingId}>編集</button>
                    <button onClick={() => handleDelete(jt.id, jt.name)} style={styles.deleteBtn}>削除</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default JobTypeSettings;